<template>
  <div
    ref="tabs"
    class="AiTabs"
    :class="{ [`AiTabs--${size}`]: Boolean(size) }"
    :role="!anchors ? `tablist` : undefined">
    <template v-if="!anchors">
      <ai-button
        v-for="item in items"
        :key="item.value"
        :ref="`tab` + item.value"
        variant="tertiary"
        role="tab"
        :hover-effect="false"
        :aria-controls="'tabpanel-' + slugify(item.label)"
        :aria-selected="item.active ? true : false"
        :aria-current="item.active ? true : false"
        :tabindex="item.active ? 0 : -1"
        :aria-hidden="item.active ? false : true"
        :aria-label="item?.ariaLabel"
        @click="handleClick(item)"
        @keydown.left="previousTab"
        @keydown.right="nextTab">
        <ai-typo
          as="h3"
          :variant="tabsVariant"
          color="inherit"
          class="AiTabs-tab"
          :class="{ 'AiTabs-tab--active': item.active }">
          {{ item.label }}
        </ai-typo>
      </ai-button>
    </template>
    <template v-else>
      <ai-button
        v-for="(item, index) in items"
        :key="index"
        :url="`#` + item.value"
        variant="tertiary"
        :hover-effect="false"
        as-link
        @click="handleClick(item)">
        <ai-typo
          as="h3"
          :variant="tabsVariant"
          color="inherit"
          class="AiTabs-tab"
          :class="{ 'AiTabs-tab--active': item.active }">
          {{ item.label }}
        </ai-typo>
      </ai-button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { slugify } from '~~/helpers';

type TabItem = {
  label: string;
  value: string | number;
  ariaLabel?: string;
};

interface Props {
  anchors?: boolean;
  items: TabItem[];
  modelValue?: string | number;
  size?: 'large' | 'medium' | 'small';
  tabsVariant?: 'tab-link-01' | 'tab-link-02';
}

interface Emits {
  (event: 'update:modelValue', value: string | number): void;
}

const props = withDefaults(defineProps<Props>(), {
  anchors: false,
  size: 'medium',
  tabsVariant: 'tab-link-01',
  modelValue: undefined,
});
const emit = defineEmits<Emits>();

const items = computed(() =>
  props.items.map(item => ({
    ...item,
    active: item.value === props.modelValue,
  })),
);

const handleClick = (item: TabItem) => {
  if (item.value === props.modelValue) return;

  emit('update:modelValue', item.value);
};

const tabfocus = ref(0);
const tabs = ref();

const previousTab = () => {
  tabfocus.value--;
  if (tabfocus.value < 0) {
    tabfocus.value = tabs?.value.children.length - 1;
  }

  setFocusTab();
};
const nextTab = () => {
  tabfocus.value++;
  if (tabfocus.value >= tabs?.value.children.length) {
    tabfocus.value = 0;
  }
  setFocusTab();
};

const setFocusTab = () => {
  tabs.value.children[tabfocus.value].setAttribute('tabindex', '0');
  tabs.value.children[tabfocus.value].focus();
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';

.AiTabs {
  display: flex;
  flex-direction: row;

  &--small,
  &--medium {
    margin-right: constants.$margin-02;
  }

  &--large {
    @media (mq.$from-medium) {
      flex-direction: column;
    }
  }

  @media (mq.$from-medium) {
    gap: constants.$margin-02;
    margin-bottom: constants.$margin-02;
  }
}

.AiTabs-tab {
  position: relative;
  display: inline-block;
  margin-right: constants.$margin-02;
  margin-bottom: constants.$margin-02;
  cursor: pointer;
  color: colors.$stratos-300;
  padding-left: 0;
  padding-bottom: 4px;
  transition: all 200ms;

  @media (mq.$from-medium) {
    padding-bottom: 0;
  }

  &::after {
    position: absolute;
    content: '';
    height: 2px;
    background: colors.$gold-700;
    width: 0;
    left: 0;
    bottom: 0;
    transition: width 300ms;
  }

  .AiTabs--small &,
  .AiTabs--medium & {
    &--active {
      padding-left: 0;

      &::after {
        width: 100%;

        @media (mq.$from-medium) {
          width: 100%;
          top: unset;
          left: 0;
          padding: 0;
        }
      }
    }
  }

  &--active {
    color: colors.$stratos-900;

    @media (mq.$from-medium) {
      padding-left: 50px;
    }

    &::after {
      width: 100%;

      @media (mq.$from-medium) {
        width: 40px;
        top: 50%;
        left: 0;
      }
    }
  }
}
</style>
